'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Marquee from 'react-fast-marquee';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import { AiSolutionPageData } from '@/components/type/aiSolution';
import Container from '@/components/ui/Container';
import HeroSectionHeading from '@/components/ui/HeroSectionHeading';
import Button from '@/components/ui/Button';
import SectionHeader from '@/components/ui/SectionHeader';
import FaqsAccordion from '@/components/ui/FaqsAccordion';
import ClientTestimonial from '@/components/ui/ClientTestimonial';
import { logoIconsLight } from '@/components/utils/Constant';

type Props = {
  data: AiSolutionPageData;
};

const AiSolution = ({ data }: Props) => {
  const [activeCategory, setActiveCategory] = useState(0);
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isClickScrolling = useRef(false);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;

        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleEntries.length > 0) {
          const activeIndex = categoryRefs.current.findIndex(
            (element) => element === visibleEntries[0].target,
          );
          if (activeIndex !== -1) setActiveCategory(activeIndex);
        }
      },
      { root: null, rootMargin: '-15% 0px -65% 0px', threshold: 0 },
    );

    categoryRefs.current.forEach((el) => el && observer.observe(el));

    // user pote scroll kare to animation cancel karo
    const cancelAnim = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = null;
      isClickScrolling.current = false;
    };
    window.addEventListener('wheel', cancelAnim, { passive: true });
    window.addEventListener('touchstart', cancelAnim, { passive: true });

    return () => {
      observer.disconnect();
      cancelAnim();
      window.removeEventListener('wheel', cancelAnim);
      window.removeEventListener('touchstart', cancelAnim);
    };
  }, []);

  const handleCategoryClick = (index: number) => {
    const target = categoryRefs.current[index];
    if (!target) return;

    if (rafId.current) cancelAnimationFrame(rafId.current);

    isClickScrolling.current = true;
    setActiveCategory(index);

    const OFFSET = 40;
    const DURATION = 600;
    const startY = window.scrollY;
    const startTime = performance.now();

    // har frame par fari calculate (layout shift safe)
    const getTargetY = () =>
      target.getBoundingClientRect().top + window.scrollY - OFFSET;

    const step = (now: number) => {
      const progress = Math.min((now - startTime) / DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const y = startY + (getTargetY() - startY) * eased;

      window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior });

      if (progress < 1) {
        rafId.current = requestAnimationFrame(step);
      } else {
        rafId.current = null;
        isClickScrolling.current = false;
      }
    };

    rafId.current = requestAnimationFrame(step);
  };

  return (
    <div
      style={{
        background: data.hero.backgroundGradient,
      }}
      className="!font-opt"
    >
      <Container className="py-12 md:py-20 lg:py-[100px]">
        <div className="flex flex-col items-center justify-between gap-6 md:gap-10 lg:flex-row lg:items-center">
          <HeroSectionHeading
            heading={data.hero.heading}
            description={data.hero.description}
            btnName={data.hero.btnName}
            link={data.hero.btnLink}
            isDark={true}
            btnStyle="bg-white hover:bg-white/20 hover:backdrop-blur-lg"
            className="max-w-[750px]"
          />
          <Image
            src={data.hero.imageUrl}
            alt={data.hero.imageAlt}
            width={600}
            height={600}
            className="w-full max-w-[400px] md:max-w-[500px] lg:max-w-[600px]"
          />
        </div>
      </Container>
      <Container className="mb-12 md:mb-20 lg:mb-[100px]">
        <div className="flex w-full min-w-0 flex-col items-start gap-6 md:gap-[25px] xl:flex-row 5xl:rounded-[24px]">
          <div className="w-full shrink-0 text-center xl:max-w-[350px] xl:text-start">
            <span className="block font-opt text-sm font-normal leading-5 text-white md:text-base">
              {data?.trustedClients}
            </span>
          </div>

          <div
            className="relative w-full min-w-0 flex-1 overflow-hidden"
            style={{
              maskImage:
                'linear-gradient(90deg, transparent 0%, black 15%, black 85%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(90deg, transparent 0%, black 15%, black 85%, transparent 100%)',
            }}
          >
            <Marquee
              speed={55}
              pauseOnHover={true}
              className="w-full overflow-hidden"
            >
              <div className="flex w-max shrink-0 items-center gap-6 px-4 md:gap-8 lg:gap-10">
                {logoIconsLight?.map((item, index) => (
                  <div
                    key={index}
                    className="flex shrink-0 items-center justify-center"
                  >
                    <Image
                      src={item}
                      alt={`Trusted client ${index + 1}`}
                      width={150}
                      height={50}
                      loading="lazy"
                      className="h-[35px] w-auto max-w-[70px] shrink-0 object-contain sm:h-[40px] sm:max-w-[80px] md:h-[50px] md:max-w-[100px]"
                    />
                  </div>
                ))}
              </div>
            </Marquee>
          </div>
        </div>
      </Container>
      <div className="mb-12 px-[18px] md:mb-20 md:px-[40px] lg:mb-[100px] lg:px-0">
        <Swiper
          modules={[Autoplay]}
          slidesPerView={2}
          spaceBetween={13}
          loop={true}
          speed={600}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          breakpoints={{
            320: {
              slidesPerView: 1,
            },
            640: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 4,
            },
            1280: {
              slidesPerView: 5,
            },
          }}
          className="approach-swiper !h-full"
        >
          {data.approachCards.map((card, ind) => (
            <SwiperSlide key={ind} className="!h-auto">
              <article className="flex h-full flex-col gap-5 rounded-lg border border-white/15 bg-[#202426] p-4">
                <h3 className="border-b border-white/10 pb-3 text-lg font-medium text-white">
                  {card.title}
                </h3>

                <p className="text-base leading-relaxed text-white/75">
                  {card.description}
                </p>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="px-[18px] md:px-[40px]">
        <Container className="mx-auto mb-12 !max-w-[1200px] rounded-[20px] bg-gradient-to-r from-[#216fa7] via-[#078fca] to-[#00b4df] px-4 py-10 sm:!px-8 sm:py-12 md:mb-20 lg:mb-[100px] lg:py-14">
          <div className="grid grid-cols-2 gap-x-3 gap-y-9 lg:grid-cols-4 lg:gap-4">
            {data.stats.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col items-center justify-center text-center text-white"
              >
                <p className="text-4xl font-normal leading-none tracking-tight sm:text-5xl lg:text-[64px]">
                  {stat.value}
                </p>

                <p className="mt-2 text-sm font-normal leading-5 sm:text-base lg:whitespace-nowrap">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </div>

      <Container className="mb-12 flex flex-col gap-10 md:mb-20 md:gap-20 lg:mb-[100px]">
        <SectionHeader
          headingText={data.customAiServices.headingText}
          descriptionText={data.customAiServices.descriptionText}
          isSectionDark={true}
          className="mx-auto max-w-[900px]"
        />
        <div className="flex flex-col gap-10 lg:flex-row">
          <div className="flex h-max w-full shrink-0 flex-col gap-0 self-start rounded-[20px] border border-white/20 px-4 py-3 text-white sm:px-6 lg:sticky lg:top-10 lg:max-w-[310px]">
            {data.customAiServices.categories?.map((item, index) => (
              <button
                key={item.label}
                type="button"
                onClick={() => handleCategoryClick(index)}
                className={`border-b py-3 text-left text-sm font-normal leading-5 transition-colors duration-300 sm:py-4 sm:text-base ${
                  activeCategory === index
                    ? 'border-b-white text-white'
                    : 'border-b-transparent text-white/60 hover:text-white'
                }`}
              >
                {item?.label}
              </button>
            ))}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-10 text-white sm:gap-[60px]">
            {data.customAiServices.categories?.map((item, index) => (
              <div
                key={item.label}
                ref={(element) => {
                  categoryRefs.current[index] = element;
                }}
                className="flex scroll-mt-10 flex-col gap-6 sm:gap-10"
              >
                <span className="text-center text-2xl font-medium leading-8 sm:text-3xl sm:leading-10 lg:text-left">
                  {item?.label}
                </span>

                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {item?.services?.map((serviceItem) => (
                    <div
                      key={serviceItem.title}
                      className="flex flex-col justify-between gap-6 rounded-[20px] border border-white/20 p-4 sm:gap-10 sm:p-6"
                    >
                      <div className="flex flex-col gap-4 sm:gap-6">
                        <h3 className="text-xl font-medium leading-6 sm:text-2xl sm:leading-7">
                          {serviceItem?.title}
                        </h3>

                        <p className="text-base font-normal leading-5 sm:text-lg sm:leading-[22px]">
                          {serviceItem?.description}
                        </p>
                      </div>

                      <Button
                        btnName="View More"
                        redirectionLink={serviceItem?.href}
                        className="bg-white"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
      <div className="pb-12 md:pb-20 lg:pb-[100px]">
        <div className="relative w-full overflow-hidden bg-project-ai">
          <Container className="relative flex flex-col items-center gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-6 xl:gap-10">
            <HeroSectionHeading
              heading={data.readyToTurnAi.heading}
              description={data.readyToTurnAi.description}
              btnName={data.readyToTurnAi.btnName}
              link={data.readyToTurnAi.btnLink}
              className="relative z-10 w-full !max-w-[979px] items-center py-12 text-center md:py-16 lg:min-w-0 lg:!max-w-[min(979px,58%)] lg:flex-none lg:items-start lg:py-[60px] lg:text-left xl:py-[80px] 3xl:py-[100px]"
              headingStyle="max-w-[853px] !text-[26px] !leading-[34px] sm:!text-3xl sm:!leading-10 md:!text-4xl lg:!text-[34px] lg:!leading-[42px] xl:!text-[42px] xl:!leading-[50px] 3xl:!text-[52px] 3xl:!leading-[60px]"
              descriptionStyle="!max-w-[979px] !text-base !leading-6 md:!text-lg md:!leading-7 xl:!text-[24px] xl:!leading-[34px]"
              isDark={true}
              btnStyle="bg-white hover:bg-white/30 hover:backdrop-blur-md"
            />

            {/* Image: lg+ pe content ke andar, bottom-right aligned */}
            <div className="pointer-events-none relative hidden self-stretch lg:block lg:w-[38%] xl:w-[40%]">
              <Image
                src={data.readyToTurnAi.imageUrl}
                alt={data.readyToTurnAi.imageAlt}
                fill
                sizes="(min-width: 1024px) 40vw, 0px"
                className="object-contain object-right-bottom"
              />
            </div>
          </Container>
        </div>
      </div>
      <Container className="flex flex-col gap-10">
        <SectionHeader
          headingText={data.customModelComparison.headingText}
          descriptionText={data.customModelComparison.descriptionText}
          isSectionDark={true}
          className="mx-auto max-w-[827px] max-lg:[&_h2]:text-3xl max-lg:[&_h2]:leading-tight max-sm:[&_h2]:text-2xl"
        />
        <div className="overflow-x-auto rounded-[14px] bg-gradient-to-r from-[#1769ed] via-[#1443c0] to-[#1a2c91] p-[7px]">
          <div className="grid min-w-[600px] grid-cols-4">
            {/* Header */}
            <div className="mr-3 overflow-hidden rounded-tl-[14px] rounded-tr-[12px] bg-white text-[#080808] sm:mr-5">
              <div className="flex h-[50px] items-center px-3 text-xs font-semibold sm:h-[57px] sm:px-4 sm:text-sm md:px-6 md:text-base">
                Matrix
              </div>
            </div>

            <div className="rounded-tl-[14px] bg-white text-[#080808]">
              <div className="flex h-[50px] items-center border-r border-[#879be8] px-3 text-xs font-semibold sm:h-[57px] sm:px-4 sm:text-sm md:px-6 md:text-base">
                Pre-built API
              </div>
            </div>

            <div className="bg-white text-[#080808]">
              <div className="flex h-[50px] items-center border-r border-[#879be8] px-3 text-xs font-semibold sm:h-[57px] sm:px-4 sm:text-sm md:px-6 md:text-base">
                Fine-tuned model
              </div>
            </div>

            <div className="overflow-hidden rounded-tr-[14px] bg-white text-[#080808]">
              <div className="flex h-[50px] items-center px-3 text-xs font-semibold sm:h-[57px] sm:px-4 sm:text-sm md:px-6 md:text-base">
                Custom model
              </div>
            </div>

            {/* Table rows */}
            {data.customModelComparison.comparison.map((row, index) => (
              <div key={row.label} className="contents">
                <div
                  className={`flex min-h-[55px] items-center bg-white/[0.045] px-3 text-sm font-medium text-white first:mr-3 sm:min-h-[65px] sm:px-4 sm:text-base sm:first:mr-5 md:px-6 ${
                    index === data.customModelComparison.comparison.length - 1
                      ? 'rounded-bl-[14px] rounded-br-[14px]'
                      : 'border-b border-white/[0.07]'
                  }`}
                >
                  {row.label}
                </div>

                {[row.api, row.fineTuned, row.custom].map((value, i) => (
                  <div
                    key={i}
                    className={`flex min-h-[55px] items-center border-l border-white/[0.06] bg-black/[0.12] px-3 text-sm leading-5 text-white sm:min-h-[65px] sm:px-4 sm:text-base md:px-6 ${
                      index !== data.customModelComparison.comparison.length - 1
                        ? 'border-b border-white/[0.07]'
                        : ''
                    } ${
                      index ===
                        data.customModelComparison.comparison.length - 1 &&
                      i === 2
                        ? 'rounded-br-[14px]'
                        : index ===
                              data.customModelComparison.comparison.length -
                                1 && i === 0
                          ? 'rounded-bl-[14px]'
                          : ''
                    }`}
                  >
                    {value}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Container>
      <div className="relative grid bg-gradient-to-r from-[#2787D0] via-[#168FC2] to-[#4654ED]"></div>
      <div className="relative overflow-hidden bg-black pt-12 md:pt-20 lg:pt-[100px]">
        {/* Background glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,110,135,0.28)_0%,rgba(0,0,0,0)_68%)]"
        />
        <Container className="flex flex-col gap-10 4xl:gap-[60px]">
          <SectionHeader
            isSectionDark={true}
            headingText={data.structuredProcess.headingText}
            descriptionText={data.structuredProcess.descriptionText}
            className="mx-auto max-w-[827px] items-center"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-0">
            {data.structuredProcess.steps.map((step, index) => {
              const totalRows = Math.ceil(
                data.structuredProcess.steps.length / 3,
              );
              const currentRow = Math.floor(index / 3);
              const isFirstRow = currentRow === 0;
              const isLastRow = currentRow === totalRows - 1;

              return (
                <div
                  key={step.number}
                  className={`${
                    isFirstRow
                      ? 'border-b-white/25 pb-5 lg:border-b lg:pb-10'
                      : isLastRow
                        ? 'pt-5 lg:mt-10 lg:pt-0'
                        : 'py-5 lg:my-10'
                  }`}
                >
                  <div
                    className={`flex h-full flex-col gap-3 sm:gap-4 ${
                      index % 3 === 1
                        ? 'h-full sm:px-5 lg:border-r lg:border-r-white/25 lg:px-10'
                        : index !== 2 && index !== 5
                          ? 'sm:pr-5 lg:border-r lg:border-r-white/25 lg:pr-10'
                          : 'sm:pl-5 lg:pl-10'
                    }`}
                  >
                    <span className="inline-flex h-[45px] w-max min-w-[45px] items-center justify-center rounded-[10px] bg-[#293234] px-2 text-2xl font-semibold leading-none text-white sm:h-[53px] sm:min-w-[53px] sm:text-[28px]">
                      {step.number}
                    </span>

                    <h3 className="text-xl font-semibold leading-6 text-white sm:text-2xl sm:leading-7">
                      {step.title}
                    </h3>

                    <p className="max-w-[440px] text-base leading-5 text-white/90 sm:text-lg sm:leading-6">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </div>
      <div className="overflow-visble relative my-12 text-white md:my-20 lg:my-[100px]">
        {/* Background glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,110,135,0.28)_0%,rgba(0,0,0,0)_68%)]"
        />{' '}
        <Container className="mb-12 flex flex-col gap-10 md:mb-20 lg:mb-[100px] 4xl:gap-[60px]">
          <SectionHeader
            isSectionDark={true}
            headingText={data.secureAndGovern.headingText}
            className="items-center"
          />
          {/* Left features */}
          <div className="grid grid-cols-1 items-center gap-6 sm:gap-8 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-10">
            <div className="order-2 flex flex-col justify-center gap-8 md:order-1 md:gap-10 lg:gap-12">
              {data.secureAndGovern.leftFeatures.map((item) => (
                <div key={item.title} className="flex flex-col gap-3 sm:gap-5">
                  <h3 className="text-xl font-semibold leading-6 text-white transition-colors duration-300 group-hover:text-cyan-300 sm:text-2xl sm:leading-7">
                    {item.title}
                  </h3>

                  <p className="text-sm font-normal leading-5 text-white/90 sm:text-base sm:leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Center robot */}
            <div className="relative order-1 flex items-center justify-center md:order-2">
              <div className="absolute overflow-visible rounded-full bg-cyan-400/15 blur-3xl sm:h-60 sm:w-60" />

              <div className="relative h-[220px] w-full max-w-[250px] sm:h-[250px] sm:max-w-[300px] md:h-[290px] lg:h-[310px]">
                <Image
                  src={data.secureAndGovern.centerImage.url}
                  alt={data.secureAndGovern.centerImage.alt}
                  width={600}
                  height={500}
                  className="object-contain drop-shadow-[0_0_28px_rgba(34,211,238,0.18)]"
                />
              </div>
            </div>

            {/* Right features */}
            <div className="order-3 flex flex-col justify-center gap-8 md:gap-10 lg:gap-12">
              {data.secureAndGovern.rightFeatures.map((item) => (
                <div
                  key={item.title}
                  className="group flex flex-col gap-3 sm:gap-5"
                >
                  <h3 className="text-xl font-semibold leading-6 text-white transition-colors duration-300 group-hover:text-cyan-300 sm:text-2xl sm:leading-7">
                    {item.title}
                  </h3>

                  <p className="text-sm font-normal leading-5 text-white/90 sm:text-base sm:leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>
      <Container className="mb-12 flex flex-col gap-10 md:mb-20 lg:mb-[100px] 4xl:gap-[60px]">
        <SectionHeader
          isSectionDark={true}
          headingText={data.aiTechnologies.headingText}
          descriptionText={data.aiTechnologies.descriptionText}
          className="mx-auto max-w-[827px] items-center"
        />
        <div className="grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4">
          {data.aiTechnologies.categories.map((category, index) => {
            // sm: 2 columns, lg: 4 columns
            const rowSm = Math.floor(index / 2);
            const colSm = index % 2;
            const rowLg = Math.floor(index / 4);
            const colLg = index % 4;

            const darkSm = (rowSm + colSm) % 2 === 0;
            const darkLg = (rowLg + colLg) % 2 === 0;
            const darkMobile = index % 2 === 0;

            const bg = [
              darkMobile ? 'bg-[#242424]' : 'bg-black/10',
              darkSm ? 'sm:bg-[#242424]' : 'sm:bg-black/10',
              darkLg ? 'lg:bg-[#242424]' : 'lg:bg-black/10',
            ].join(' ');

            return (
              <div
                key={category.title}
                className={`flex min-w-0 flex-col gap-3 p-6 ${bg}`}
              >
                <h3 className="text-xl font-medium leading-7 text-white">
                  {category.title}
                </h3>

                <div className="flex flex-wrap gap-3">
                  {category.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-white/[0.06] bg-[#303030] px-2.5 py-1 text-sm font-normal leading-6 text-[#e5e5e5] transition-colors duration-200 hover:bg-[#444444]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Container>
      <div className="mb-12 flex flex-col gap-10 px-4 sm:pl-6 md:mb-20 md:pl-10 lg:mb-[100px] 4xl:gap-[60px]">
        <Container>
          <SectionHeader
            headingText={data.aiIndustries.headingText}
            isSectionDark={true}
            descriptionText={data.aiIndustries.descriptionText}
            className="mx-auto max-w-[827px] items-center"
          />
        </Container>

        <div className="mx-auto w-full max-w-[1500px] px-4 sm:px-6 xl:px-10">
          <Swiper
            modules={[Autoplay]}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            rewind={true}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              768: { slidesPerView: 2, spaceBetween: 20 },
              1280: { slidesPerView: 3, spaceBetween: 20 },
            }}
            className="ai-project-swiper !h-full"
          >
            {data.aiIndustries.cards.map((card, index) => (
              <SwiperSlide
                key={`${card.title}-${index}`}
                className="!h-auto rounded-2xl border border-white/25"
              >
                <div className="flex h-full flex-col gap-2 sm:gap-3">
                  <Image
                    src={card.image}
                    alt={card.title}
                    width={500}
                    height={500}
                    className="w-full rounded-2xl object-contain"
                  />
                  <div className="flex h-full flex-col justify-between gap-4 p-4 sm:gap-5 sm:p-6">
                    <div className="flex flex-col gap-3 sm:gap-5">
                      <h3 className="text-xl font-semibold leading-6 text-white sm:text-2xl sm:leading-7">
                        {card.title}
                      </h3>
                      <p className="text-sm font-normal leading-5 text-white/80 sm:text-base">
                        {card.description}
                      </p>
                    </div>

                    <ul className="flex flex-col gap-3 sm:gap-4">
                      {card.services.map((service) => (
                        <li
                          key={service}
                          className="text-sm font-medium leading-5 text-white sm:text-base"
                        >
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div className="flex flex-col gap-10 pl-4 sm:pl-6 md:pl-10 4xl:gap-[60px]">
        <Container>
          <SectionHeader
            isSectionDark={true}
            headingText={data.whyChooseUs.headingText}
            descriptionText={data.whyChooseUs.descriptionText}
            className="mx-auto max-w-[827px] items-center"
          />
        </Container>
        <div>
          <Swiper
            modules={[Autoplay]}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: {
                slidesPerView: 1.5,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1100: {
                slidesPerView: 3,
                spaceBetween: 28,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 32,
              },
            }}
            className="whyChooseUs-swiper !h-full"
          >
            {data.whyChooseUs.items.map((item, index) => (
              <SwiperSlide
                key={index}
                className="!h-auto rounded-xl bg-whyChooseUs p-4 sm:p-6"
              >
                <div className="flex h-full flex-col gap-2 sm:gap-3">
                  <h3 className="text-lg font-semibold leading-6 text-white sm:text-xl sm:leading-7">
                    {item.title}
                  </h3>

                  <p className="text-sm font-normal leading-5 text-white/80 sm:text-base">
                    {item.description}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <Container>
        <ClientTestimonial
          isCardBg={true}
          headingText={'WHAT CLIENTS SAY'}
          headingStyle={'!text-white'}
          className={'mx-auto max-w-[827px] items-center'}
          descriptionText={`Behind every testimonial is a business problem solved, a system improved, or a product
                                           successfully launched. Here is how our clients describe their experience working with
                                           Optimity Logics.`}
          descriptionStyle={'!text-white'}
        />
      </Container>

      <Container className="!max-w-[1200px] pb-12 md:pb-16 lg:pb-20">
        <FaqsAccordion data={data?.faqs} isDarkbg={true} />
      </Container>
    </div>
  );
};

export default AiSolution;
